import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isUndefined from 'lodash/isUndefined';
import { ILookUpUserApiResponseProps } from '../api/lookup';
import {
    SIGN_IN_METHOD_APPLE_LOGIN,
    SIGN_IN_METHOD_FACEBOOK_LOGIN,
    SIGN_IN_METHOD_PASSWORD,
} from '../constants';
import {
    EXTERNAL_USER_EMAIL_USED_ON_SOFT_ACCOUNT_ERROR,
    LOOKUP_SOCIAL_USER_EXISTENT,
    LOOKUP_SOCIAL_USER_EXISTENT_IN_SOFT_ACCOUNT,
    LOOKUP_SOCIAL_USER_UNEXISTENT,
} from '../constants/apiErrors';
import { IAllContextsProps } from '../constants/interfaces';
import {
    EMAIL_UNEXISTENT_ERROR_MESSAGE,
    NOT_EMAIL_ASSOCIATED_MESSAGE,
    ONLY_SOCIAL_LOGIN_ERROR_MESSAGE,
    SOCIAL_ACCOUNT_ALREADY_LINKED_ERROR_MESSAGE,
} from '../constants/lookupMessages';
import { CREATE_ACCOUNT_MESSAGE } from '../constants/messages';
import { IConfigContextProps } from '../contexts/ConfigContext';
import { IFrontendContext, MODAL_TYPES } from '../contexts/FrontendContext';
import { INotificationContext } from '../contexts/NotificationsContext';
import { IUserContext } from '../contexts/UserContext';
import {
    trackEnterEmailExistingAccount,
    trackEnterEmailNewAccount,
    trackEnterEmailSoftAccount,
} from '../tracking/login';
import { trackSIWA_EmailNotFound } from '../tracking/social';
import { socialProviderCheckers } from '../utils/social';
import { handleSocialLogInErrors } from './socialErrors';
import {
    checkSocialConnections,
    storeSocialDataOnRedis,
} from './socialHandlers';

interface IHandleUserIsSocialProps {
    signInMethods?: string[];
    contexts: IAllContextsProps;
}

export const handleUserIsSoftAccount = ({
    contexts,
}: IHandleUserIsSocialProps) => {
    const { notificationsContext, configContext, frontendContext } = contexts;
    const analytics = configContext?.analytics;
    const externalActions = configContext?.lookupMethods;

    trackEnterEmailSoftAccount({ analytics });

    externalActions?.success?.WITH_USER_ID?.SOFT_ACCOUNT?.action?.();

    const notificationConfig = externalActions?.success?.WITH_USER_ID?.SOFT_ACCOUNT?.getNotification?.();

    if (notificationConfig) {
        notificationsContext?.methods.setGeneralNotification({
            extraMessage: notificationConfig.extraMessage,
            cta: {
                action: notificationConfig?.ctaAction,
                message: notificationConfig?.ctaMessage,
            },
        });
    } else {
        frontendContext?.methods.setShowModal(
            MODAL_TYPES.softAccountActivation,
        );
    }
};

export const handleUserIsSocial = ({
    signInMethods,
    contexts,
}: IHandleUserIsSocialProps) => {
    const { configContext } = contexts;
    const externalActions = configContext?.lookupMethods;
    const userIsSocialConfig =
        externalActions?.success?.WITH_USER_ID?.SOCIAL_ACCOUNT;

    userIsSocialConfig?.action?.({
        signInMethods,
    });

    const notificationConfig = userIsSocialConfig?.getNotification?.({
        signInMethods,
    });
    const extraSocialNotificationProps = {
        extraMessage: notificationConfig?.extraMessage,
        cta: {
            action: notificationConfig?.ctaAction,
            message: notificationConfig?.ctaMessage ?? CREATE_ACCOUNT_MESSAGE,
        },
    };

    checkSocialConnections({
        signInMethods,
        extraSocialNotificationProps,
        useLookupSigninMethodSyntax: true,
        contexts,
    });
};

export const handleUserNonExistent = ({
    contexts,
}: {
    contexts: IAllContextsProps;
}) => {
    const { notificationsContext, configContext, userContext } = contexts;
    const externalActions = configContext?.lookupMethods;
    const analytics = configContext?.analytics;

    userContext?.methods.lookup.setExistentAccount('');

    trackEnterEmailNewAccount({ analytics });

    externalActions?.success?.WITHOUT_USER_ID?.action?.();

    const notificationConfig = externalActions?.success?.WITHOUT_USER_ID?.getNotification?.();

    notificationsContext?.methods.setGeneralNotification({
        message: NOT_EMAIL_ASSOCIATED_MESSAGE,
        extraMessage: notificationConfig?.extraMessage,
        cta: {
            action: notificationConfig?.ctaAction,
            message: notificationConfig?.ctaMessage ?? CREATE_ACCOUNT_MESSAGE,
        },
    });
};

export const handleLookupUserResponseForLinking = ({
    response,
    contexts,
    accessToken,
    email,
}: {
    response: { user_id: string; sign_in_methods: string[] };
    email: string;
    accessToken: string;
    contexts: IAllContextsProps;
}) => {
    const setGeneralNotification =
        contexts?.notificationsContext?.methods.setGeneralNotification;
    const setExistentAccount =
        contexts?.userContext?.methods?.lookup?.setExistentAccount;
    const socialProvider = contexts?.userContext?.state?.socialProvider;
    const analytics = contexts?.configContext?.analytics;

    setGeneralNotification?.({});

    // TODO: Find a way to have pathname within contexts
    // const state = getState();
    // const {
    //     routing: {
    //         locationBeforeTransitions: { pathname },
    //     },
    // } = state;

    const { user_id: userId, sign_in_methods: signInMethods } = response;
    /*
    The subsequent routes (socialLogin) expect email to be in state, and the
    account status to be checked, so need to update store before dealing with the response
    logic. When setting account status as checked, the email reducer is
    also listening and will update.
    */

    setExistentAccount?.(email);
    if (
        isUndefined(userId)
        // && pathname === `${BASE_URL}/${SOCIAL_LOOK_UP_PATH}`
    ) {
        trackSIWA_EmailNotFound({ analytics });

        setGeneralNotification?.({
            message: EMAIL_UNEXISTENT_ERROR_MESSAGE,
        });
    } else if (
        !!userId &&
        includes(signInMethods, SIGN_IN_METHOD_APPLE_LOGIN)
    ) {
        // for now, we are asking for EB account linking only in the SignIn with Apple flow,
        // in the future, when we ask for linking for other providers we should redefine this condition

        setGeneralNotification?.({
            message: SOCIAL_ACCOUNT_ALREADY_LINKED_ERROR_MESSAGE,
        });
    } else if (!!userId && includes(signInMethods, SIGN_IN_METHOD_PASSWORD)) {
        // dispatch(updateFormType(SOCIAL_LOG_IN_PATH));

        /**
         * Storing the new entered email and the access_token from siwa
         * on redis for the forgotPassword flow.
         */

        socialProvider
            ? storeSocialDataOnRedis({ email, accessToken, socialProvider })
            : null;
    } else if (
        !!userId &&
        isEqual(signInMethods, [SIGN_IN_METHOD_FACEBOOK_LOGIN])
    ) {
        // if only has facebook as login method, we cannot link their account asking for email+password

        setGeneralNotification?.({
            message: ONLY_SOCIAL_LOGIN_ERROR_MESSAGE,
        });
    } else if (!!userId && isEmpty(signInMethods)) {
        handleSocialLogInErrors({
            contexts,
            socialProvider,
            responseErrorObj: {
                __all__: [EXTERNAL_USER_EMAIL_USED_ON_SOFT_ACCOUNT_ERROR],
            },
        });
    }
};

/**
 * This function should not do Promise.reject() as that would trigger the catch of the lookup action.
 * Use Promise.resolve({ error: 'error code' }) to let functions using this method how to proceed with
 * the response.
 * Handles the response from the api if the user is trying to Login or Signup using the Social Buttons
 */
export const handleLookupUserResponseSocial = ({
    response,
    socialProvider,
    email,
    contexts,
}: {
    response: ILookUpUserApiResponseProps;
    socialProvider: string;
    email: string;
    contexts: {
        frontendContext?: IFrontendContext;
        notificationsContext?: INotificationContext;
        configContext?: IConfigContextProps;
        userContext?: IUserContext;
    };
}) => {
    const { userId, signInMethods } = response;
    const analytics = contexts.configContext?.analytics;
    const socialSSOSettings = contexts.configContext?.social?.SSOSettings;
    const socialProviderChecker = socialProviderCheckers[socialProvider];

    /*
    The subsequent routes (login/signup/etc) expect email to be in state, and the
    account status to be checked, so need to update store before dealing with the response
    logic. When setting account status as checked, the email reducer is
    also listening and will update.
    */
    contexts?.userContext?.methods.lookup.setExistentAccount(email);

    if (!!userId) {
        // If user has account linked to this social provider we return and continue with the login
        if (socialProviderChecker?.({ socialSSOSettings, signInMethods })) {
            // We reject the function so it enters the catch on appleLogin.
            // Probably it is better to provide some kind of error to ensure a proper treatment
            return Promise.resolve({
                error: { message: LOOKUP_SOCIAL_USER_EXISTENT },
            });
        }

        if (signInMethods.includes(SIGN_IN_METHOD_PASSWORD)) {
            trackEnterEmailExistingAccount({ analytics });
            // Returning this will redirect us to LINK_ACCOUNTS_LOG_IN_PATH, that is the look familiar
            // page with the new design.
            return Promise.resolve({
                error: { message: LOOKUP_SOCIAL_USER_EXISTENT },
            });
            /* These 3 options are not reachable.
             * - socialProviderChecker will catch any socially connected account.
             * - the include above will catch any account with password
             * - the else statement will catch soft accounts.
             */
            // } else if (hasFbAccount({ socialSSOSettings, signInMethods })) {
            //     trackAccountAlreadyConnectedWithSocials({
            //         analytics,
            //         connectedSocials: [GA_FACEBOOK],
            //     });

            //     return Promise.resolve({
            //         error: {
            //             message: LOOKUP_SOCIAL_USER_EXISTENT_IN_FACEBOOK_SOFT_ACCOUNT,
            //         },
            //     });
            // } else if (hasGoogleAccount({ socialSSOSettings, signInMethods })) {
            //     trackAccountAlreadyConnectedWithSocials({
            //         analytics,
            //         connectedSocials: [GA_GOOGLE],
            //     });

            //     return Promise.resolve({
            //         error: {
            //             message: LOOKUP_SOCIAL_USER_EXISTENT_IN_GOOGLE_SOFT_ACCOUNT,
            //         },
            //     });
            // } else if (hasAppleAccount({ socialSSOSettings, signInMethods })) {
            //     trackAccountAlreadyConnectedWithSocials({
            //         analytics,
            //         connectedSocials: [GA_APPLE],
            //     });

            //     return Promise.resolve();
        } else {
            //User has a Soft account
            // We reject the function so it enters the catch on appleLogin.
            // Probably it is better to provide some kind of error to ensure a proper treatment
            return Promise.resolve({
                error: {
                    message: LOOKUP_SOCIAL_USER_EXISTENT_IN_SOFT_ACCOUNT,
                },
            });
        }
    } else {
        // TODO: this should change to SocialLookupPage but the modal is preventing this.
        // dispatch(updateFormType(SOCIAL_LOOK_UP_PATH));

        // The account is not existent so we ask the user what should he do.
        // Resolving the promise with an error will allow us to trigger the socialLoginAction
        // and store whatever we need in redis.
        return Promise.resolve({
            error: { message: LOOKUP_SOCIAL_USER_UNEXISTENT },
        });
    }
};
